import type {Copilot} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import type {AssigneePickerBaseAssignee} from '@github-ui/item-picker-assignee'
import {DEFAULT_MAXIMUM_ASSIGNEES} from '@github-ui/item-picker-assignee'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate} from 'react-relay/hooks'

import {addAgentAssignmentToIssueLocally, addAssigneesToIssueLocally} from './relay-tsq-interop'

type UseAgentAssignmentCompleteOptions = {
  issueId: string
  onIssueUpdate?: () => void
}

/**
 * Returns an `onAssignmentComplete` callback for use with `CopilotAssignmentModal`.
 *
 * After a successful agent assignment, the callback updates the Relay store to:
 * 1. Add the agent as an assignee
 * 2. Add the viewer as an assignee (if not already assigned and under the limit)
 * 3. Record the agent assignment with the viewer as the assigner
 *
 * All data (current assignees, viewer) is read directly from the Relay store.
 */
export function useAgentAssignmentComplete({issueId, onIssueUpdate}: UseAgentAssignmentCompleteOptions) {
  const environment = useRelayEnvironment()

  return useCallback(
    (agent?: Copilot) => {
      if (agent) {
        commitLocalUpdate(environment, store => {
          const agentAssignee: AssigneePickerBaseAssignee = {
            ...agent,
            isCopilot: isCopilot(agent.login),
            isAgent: true,
            __typename: 'Bot',
          }

          const assigneesToAdd: AssigneePickerBaseAssignee[] = [agentAssignee]

          // Read current assignees and viewer from the store
          const issueRecord = store.get(issueId)
          const assignedActors = issueRecord?.getLinkedRecord('assignedActors(first:20)')
          const currentNodes = assignedActors?.getLinkedRecords('nodes') || []
          // safeViewer is used by the issue page query; fall back to viewer for other contexts
          const viewerRecord =
            store.getRoot().getLinkedRecord('safeViewer') || store.getRoot().getLinkedRecord('viewer')

          if (viewerRecord) {
            const viewerLogin = viewerRecord.getValue('login') as string
            const isViewerAlreadyAssigned = currentNodes.some(node => node && node.getValue('login') === viewerLogin)
            const shouldAddViewer = !isViewerAlreadyAssigned && currentNodes.length + 1 < DEFAULT_MAXIMUM_ASSIGNEES
            if (shouldAddViewer) {
              assigneesToAdd.push({
                id: viewerRecord.getValue('id') as string,
                login: viewerLogin,
                name: viewerRecord.getValue('name') as string | undefined,
                avatarUrl: viewerRecord.getValue('avatarUrl') as string,
                __typename: viewerRecord.getValue('__typename') as string,
                profileResourcePath: viewerRecord.getValue('profileResourcePath') as string | undefined,
              })
            }
          }

          addAssigneesToIssueLocally(store, issueId, assigneesToAdd)

          if (viewerRecord) {
            addAgentAssignmentToIssueLocally(store, issueId, agent, {
              id: viewerRecord.getValue('id') as string,
              login: viewerRecord.getValue('login') as string,
              __typename: viewerRecord.getValue('__typename') as string,
            })
          }
        })
      }

      onIssueUpdate?.()
    },
    [environment, issueId, onIssueUpdate],
  )
}
